import { Box3, Matrix4, type Mesh, type Object3D, Vector3 } from "three";
import { PRODUCT_SCENE_LOADING_BOUNDS_NAME } from "../new/productViewerCameraFit";

/**
 * Whether a mesh under a part group describes the part's real shape.
 *
 * Two kinds of mesh have to be kept out of every measurement taken here. The
 * drag helpers - the glow clones, the previous frame's arrows - are the part's
 * own decorations and would grow the measurement without bound as they are fed
 * back into it. The loading placeholder is the stand-in a part shows while its
 * model downloads: a box built from the option's declared bounds, or nothing at
 * all when no bounds were recorded. Measuring it yields a fit for a crate the
 * part never had, and since the real meshes are swapped in by a loader rather
 * than by a React render, that wrong fit would simply stay.
 */
export function isRealPartMesh(object: Object3D): object is Mesh {
  const mesh = object as Mesh;
  if (!mesh.isMesh || !mesh.geometry) return false;
  if (mesh.userData?.isDragHelper) return false;
  if (mesh.name === PRODUCT_SCENE_LOADING_BOUNDS_NAME) return false;
  return true;
}

/**
 * Identity of the geometry currently mounted under a part, used to notice that
 * a model has finished downloading and replaced its placeholder. A part's
 * meshes are swapped in by a loader rather than by a React render, so there is
 * no prop or state change to key off - the consumers have to look.
 */
export function meshSignature(target: Object3D | null): string {
  if (!target) return "";
  let signature = "";
  target.traverse((object) => {
    if (!isRealPartMesh(object)) return;
    signature += `${object.geometry.uuid};`;
  });
  return signature;
}

/**
 * Bounding box of the part's real geometry, in the frame its arrows are drawn
 * in. Returns null until the part has geometry worth measuring - see
 * {@link isRealPartMesh} for what is left out and why.
 */
export function measurePartBox(group: Object3D | null): Box3 | null {
  if (!group) return null;
  group.updateWorldMatrix(true, true);
  const toArrowFrame = new Matrix4();
  if (group.parent) {
    toArrowFrame.copy(group.parent.matrixWorld).invert();
  }

  const box = new Box3();
  const meshBox = new Box3();
  const matrix = new Matrix4();
  group.traverse((object) => {
    if (!isRealPartMesh(object)) return;
    if (!object.geometry.boundingBox) object.geometry.computeBoundingBox();
    const geometryBox = object.geometry.boundingBox;
    if (!geometryBox) return;
    meshBox.copy(geometryBox);
    matrix.multiplyMatrices(toArrowFrame, object.matrixWorld);
    meshBox.applyMatrix4(matrix);
    box.union(meshBox);
  });

  return box.isEmpty() ? null : box;
}

/**
 * How far the part reaches from `origin` in `direction` — the support function
 * of its bounding box. This is where an arrow pointing that way has to start:
 * the arrows are depth-tested, so one beginning inside the part is swallowed by
 * it, and a wide handle would silently lose the two arrows across its length.
 */
export function partReachAlong(
  box: Box3 | null,
  origin: Vector3,
  direction: Vector3,
): number {
  if (!box) return 0;
  const corner = new Vector3();
  let reach = 0;
  for (const x of [box.min.x, box.max.x]) {
    for (const y of [box.min.y, box.max.y]) {
      for (const z of [box.min.z, box.max.z]) {
        corner.set(x, y, z).sub(origin);
        reach = Math.max(reach, corner.dot(direction));
      }
    }
  }
  return reach;
}
